import {
  useState, useMemo 
} from 'react';
import {
  useLocation, useNavigate 
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useStockLevels } from '@/features/inventory/hooks';
import { getStockStatus } from '@/features/inventory/types';
import { toast } from 'sonner';
import {
  DEFAULT_SITE_ID, TABS 
} from './inventory-constants';
import { StockView } from './stock-view';
import { StorageView } from './storage-view';
import { TransfersView } from './transfers-view';
import { CountsView } from './counts-view';

export function InventoryPage() {
  const { t } = useTranslation('inventory');
  const location = useLocation();
  const navigate = useNavigate();
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [adjustFormData, setAdjustFormData] = useState({
    itemId: '',
    adjustmentType: 'add' as 'add' | 'remove' | 'set',
    quantity: '',
    reason: '',
  });
  const [transferFormData, setTransferFormData] = useState({
    fromVenue: '',
    toVenue: '',
    itemId: '',
    quantity: '',
  });

  const activeTab = useMemo(() => {
    if (location.pathname.includes('/storage')) return 'storage';
    if (location.pathname.includes('/transfers')) return 'transfers';
    if (location.pathname.includes('/counts')) return 'counts';
    return 'stock';
  }, [location.pathname]);

  const {
    data, isLoading, error, refetch, isFetching 
  } = useStockLevels({
    siteId: DEFAULT_SITE_ID,
    filters: { lowStockOnly },
  });

  const items = data?.data ?? [];
  const totalItems = items.length;
  const lowStockItems = items.filter((item) => getStockStatus(item) === 'low-stock').length;
  const outOfStockItems = items.filter((item) => getStockStatus(item) === 'out-of-stock').length;

  const handleAdjustStock = () => {
    if (selectedItems.length === 1) {
      const item = items.find((i) => i.itemId === selectedItems[0]);
      if (item) {
        setAdjustFormData({
          itemId: item.itemId,
          adjustmentType: 'add',
          quantity: '',
          reason: '' 
        });
        setShowAdjustModal(true);
      }
    }
  };

  const handleSaveAdjustment = () => {
    if (!adjustFormData.quantity || !adjustFormData.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    toast.success('Stock adjusted successfully');
    setShowAdjustModal(false);
    setSelectedItems([]);
    refetch();
  };

  const handleCreateTransfer = () => {
    setTransferFormData({
      fromVenue: '',
      toVenue: '',
      itemId: '',
      quantity: '' 
    });
    setShowTransferModal(true);
  };

  const handleSaveTransfer = () => {
    if (!transferFormData.fromVenue || !transferFormData.toVenue || !transferFormData.itemId || !transferFormData.quantity) {
      toast.error('Please fill in all required fields');
      return;
    }
    toast.success('Transfer created successfully');
    setShowTransferModal(false);
    refetch();
  };

  const handleBulkUpload = (file: File) => {
    toast.success(`Processing ${file.name}...`);
    setTimeout(() => {
      toast.success('Updated 50 stock levels');
      refetch();
    }, 1500);
  };

  const toggleItemSelection = (itemId: string) => {
    setSelectedItems((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const exportData = items.map((item) => ({
    itemId: item.itemId,
    name: item.itemName,
    sku: item.sku,
    currentStock: item.currentQuantity,
    unit: item.unit,
    reorderPoint: item.reorderPoint,
    location: item.storageLocationName,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <p className="text-muted-foreground">{t('subtitle')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="border-b">
        <nav className="flex gap-4">
          {TABS.map((tab) => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => navigate(tab.href)}
                className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground'
                }`}
              >
                <TabIcon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'stock' && (
        <StockView
          items={items}
          isLoading={isLoading}
          error={error}
          totalItems={totalItems}
          lowStockItems={lowStockItems}
          outOfStockItems={outOfStockItems}
          lowStockOnly={lowStockOnly}
          setLowStockOnly={setLowStockOnly}
          selectedItems={selectedItems}
          toggleItemSelection={toggleItemSelection}
          showAdjustModal={showAdjustModal}
          setShowAdjustModal={setShowAdjustModal}
          adjustFormData={adjustFormData}
          setAdjustFormData={setAdjustFormData}
          onAdjustStock={handleAdjustStock}
          onSaveAdjustment={handleSaveAdjustment}
          onBulkUpload={handleBulkUpload}
          onRefetch={refetch}
          exportData={exportData}
        />
      )}

      {activeTab === 'storage' && <StorageView />}

      {activeTab === 'transfers' && (
        <TransfersView
          items={items}
          showTransferModal={showTransferModal}
          setShowTransferModal={setShowTransferModal}
          transferFormData={transferFormData}
          setTransferFormData={setTransferFormData}
          onCreateTransfer={handleCreateTransfer}
          onSaveTransfer={handleSaveTransfer}
        />
      )}

      {activeTab === 'counts' && <CountsView />}
    </div>
  );
}
